<script setup>
// Reusable content card (Figma node 75-3675 lineage). Three variants:
//   arrow   — whole card is a link, colour fill, circular arrow chip top-right
//   splash  — whole card is a link, cream fill, image splash left/right (4:2
//             content:image ratio), text CTA instead of the arrow chip
//   program — whole card is a link, colour fill, Feature tonal illustration
//             splash left/right (jk-21 follow-up, 2026-08-07 — replaces the
//             old "plain" variant: not a link, optional icon, inert text CTA;
//             the Academy Programs cards were its only consumer, and none of
//             that behaviour survived the redesign, so it was renamed rather
//             than kept alongside an actually-dead "plain" mode). Circular
//             arrow chip fixed at the card's own top-right corner (unlike
//             `arrow`'s, which sits in a header row) since the illustration/
//             text split alternates sides per card; hover lightens the whole
//             card and nudges it right, with the arrow chip nudging further
//             still — a different feel from `arrow`/`splash`'s opacity-fade.
// Text colour is decided here from `colour`, not passed in — light fills
// (pink/yellow/lime/cream) get dark ink text, dark fills (green/blue) get
// light cream text, so callers can't accidentally pick an illegible pairing.
import IllustrationSplash from './IllustrationSplash.vue'
import { withTrailingSlash } from '@/utils/siteUrl'

const props = defineProps({
  variant:       { type: String, default: 'arrow' },   // arrow | splash | program
  colour:        { type: String, default: 'pink' },     // pink | green | cream | yellow | lime | blue
  title:         { type: String, required: true },
  body:          { type: String, default: '' },
  to:            { type: String, default: '' },         // required for arrow/splash/program
  cta:           { type: String, default: 'Learn more' }, // splash CTA text; also program's aria-label wording
  image:         { type: Object, default: null },       // { src, alt } — splash only
  imagePosition: { type: String, default: 'left' },      // left | right — splash or program
  imageFit:      { type: String, default: 'cover' },     // cover | contain — splash only ('contain' for decorative illustration tiles)
  illoVariation: { type: String, default: 'A' },         // program only — a Feature tonal (IllustrationSplash) variation letter
})

const isExternal = (to) => typeof to === 'string' && /^https?:\/\//.test(to)

// The illustration's safe (line-free) edge must face the text, which sits on
// the *opposite* side from the media panel — same reasoning as ContentHero's
// contentSide, just driven by imagePosition instead of a fixed layout.
const illoContentSide = (position) => (position === 'right' ? 'left' : 'right')
</script>

<template>
  <component
    :is="isExternal(to) ? 'a' : 'RouterLink'"
    v-bind="isExternal(to)
      ? { href: to, target: '_blank', rel: 'noopener noreferrer' }
      : { to: withTrailingSlash(to) }"
    class="content-card"
    :class="[colour, variant, (variant === 'splash' || variant === 'program') ? imagePosition : null]"
    :aria-label="variant === 'program' ? `${cta} about ${title}` : undefined"
  >
    <template v-if="variant === 'splash'">
      <template v-if="imagePosition === 'right'">
        <div class="text">
          <h3 class="title">{{ title }}</h3>
          <p v-if="body" class="body">{{ body }}</p>
          <span class="link">{{ cta }} <span class="arrow" aria-hidden="true">→</span></span>
        </div>
        <div class="media"><img :src="image.src" :alt="image.alt" loading="lazy" :style="{ objectFit: imageFit }" /></div>
      </template>
      <template v-else>
        <div class="media"><img :src="image.src" :alt="image.alt" loading="lazy" :style="{ objectFit: imageFit }" /></div>
        <div class="text">
          <h3 class="title">{{ title }}</h3>
          <p v-if="body" class="body">{{ body }}</p>
          <span class="link">{{ cta }} <span class="arrow" aria-hidden="true">→</span></span>
        </div>
      </template>
    </template>

    <template v-else-if="variant === 'program'">
      <template v-if="imagePosition === 'right'">
        <div class="text">
          <h3 class="title">{{ title }}</h3>
          <p v-if="body" class="body">{{ body }}</p>
        </div>
        <div class="media">
          <IllustrationSplash
            family="featureTonal"
            :variation="illoVariation"
            :colour="colour"
            :contentSide="illoContentSide('right')"
            class="splash-illo"
          />
        </div>
      </template>
      <template v-else>
        <div class="media">
          <IllustrationSplash
            family="featureTonal"
            :variation="illoVariation"
            :colour="colour"
            :contentSide="illoContentSide('left')"
            class="splash-illo"
          />
        </div>
        <div class="text">
          <h3 class="title">{{ title }}</h3>
          <p v-if="body" class="body">{{ body }}</p>
        </div>
      </template>
      <!-- Decorative — the link's own aria-label above carries the accessible name. -->
      <span class="arrow-btn" aria-hidden="true">
        <svg viewBox="0 0 20 20" fill="none">
          <path d="M4 10h11m-4-5 5 5-5 5" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </span>
    </template>

    <template v-else>
      <div class="header-row">
        <h3 class="title">{{ title }}</h3>
        <span class="arrow-btn" aria-hidden="true">
          <svg viewBox="0 0 20 20" fill="none">
            <path d="M4 10h11m-4-5 5 5-5 5" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
      </div>
      <p v-if="body" class="body">{{ body }}</p>
    </template>
  </component>
</template>

<style lang="scss" scoped>
.content-card {
  text-decoration: none;

  .title {
    margin: 0;
    font-size: $font-size-h4;
    line-height: $lh-heading-sm;
    letter-spacing: $ls-heading;
    font-weight: 500;
  }

  .body {
    margin: 0;
    font-size: $font-size-lg;
    line-height: $lh-base;
  }

  // ─── arrow / splash — whole-card link states ────────────────────────────
  &.arrow, &.splash {
    @include transition(opacity, transform);

    &:hover          { opacity: .9; }
    &:focus-visible  { @include focus-ring; outline: none; }
    &:active         { transform: translateY(1px); }
  }

  // ─── arrow variant ───────────────────────────────────────────────────────
  &.arrow {
    display: flex;
    flex-direction: column;
    gap: $space-5;
    padding: $space-6;
    border: 1px solid var(--color-border-decorative);
    @include rounded($radius);

    &:hover .arrow-btn { transform: translateX(2px); }

    .header-row {
      display: flex;
      align-items: center;
      gap: $space-4;

      .title { flex: 1; min-width: 0; }
    }

    // Decorative circular arrow chip (36px).
    .arrow-btn {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: $radius-pill;
      @include transition(transform);

      svg { width: 20px; height: 20px; display: block; }
    }
  }

  // ─── splash variant ──────────────────────────────────────────────────────
  &.splash {
    display: grid;
    align-items: stretch;
    gap: $space-5;
    @include rounded($radius);
    overflow: hidden;

    // Single column below from-md (jk-34 — this grid had no mobile
    // treatment at all; media/text were squeezed into 2fr/4fr fractions of
    // a phone-width card). DOM order already puts media/text in the right
    // reading order for each side, so a straight 1-column collapse is enough.
    grid-template-columns: 1fr;

    @include from-md {
      &.left  { grid-template-columns: 2fr 4fr; }   // media first — narrower image column
      &.right { grid-template-columns: 4fr 2fr; }   // text first — narrower image column
    }

    .media {
      aspect-ratio: 4 / 3;

      img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    // Cream regardless of `colour` — colour lives on the image side only,
    // the text panel stays a consistent, always-legible cream+ink pairing.
    .text {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: $space-3;
      padding: $space-6;
      background: var(--color-card-cream);
      color: var(--color-ink);
    }

    .link {
      display: inline-flex;
      align-items: center;
      gap: $space-1;
      font-weight: 500;
      text-decoration: underline;
      text-underline-offset: 3px;

      .arrow { line-height: 1; }
    }
  }

  // ─── program variant — whole card is a link, Feature tonal illustration
  // splash left/right, circular arrow chip fixed at the card's own top-right
  // corner (unlike `arrow`'s header-row placement — this card's content
  // alternates sides, but the chip stays put). Hover lightens + nudges the
  // whole card right; the chip nudges further still (nested transforms —
  // its own translateX stacks on top of the card's).
  &.program {
    position: relative;
    display: grid;
    align-items: stretch;
    gap: $space-5;
    @include rounded($radius);
    overflow: hidden;
    @include transition(transform, filter);

    // Single column below from-md (jk-34 — same missing-mobile-treatment
    // bug as .splash above).
    grid-template-columns: 1fr;

    @include from-md {
      &.left  { grid-template-columns: 2fr 4fr; }   // media first — narrower image column
      &.right { grid-template-columns: 4fr 2fr; }   // text first — narrower image column
    }

    &:hover           { filter: brightness(1.06); transform: translateX(4px); }
    &:hover .arrow-btn { transform: translateX(8px); }   // 4px own + the card's own 4px shift = 8px total
    &:focus-visible   { @include focus-ring; outline: none; }
    &:active          { transform: translateY(1px); }

    .media {
      aspect-ratio: 4 / 3;

      // Overrides IllustrationSplash's own default aspect-ratio (546:464)
      // so it fills .media's actual box exactly — same technique as
      // ContentHero.vue/ImpactCard.vue's illustration overrides.
      .splash-illo {
        width: 100%;
        height: 100%;
        aspect-ratio: auto;
      }

      // Direct instruction: the Feature tonal splash is dropped entirely
      // below $bp-md (768px) on these cards ("Learn more about..."
      // cross-link blocks + the Academy Programs landing page) — text-only
      // at phone/small-tablet widths, no decorative illustration.
      @media (max-width: 767.98px) {
        display: none;
      }
    }

    .text {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: $space-3;
      padding: $space-5;
      // Clears the arrow chip, fixed at the card's own top-right — only
      // actually needed when .text is the right-hand column and sits under
      // it (imagePosition="left", .media on the left).
      padding-right: $space-5 + 40px;
    }

    // `.right` cards put .text on the *left* column instead (see
    // grid-template-columns above) — the chip sits over .media there, so
    // .text needs no right clearance (jk-34, this used to apply
    // unconditionally regardless of side); padding-left widened instead for
    // breathing room against the card's rounded left edge. from-md-gated,
    // matching the breakpoint the two-column layout itself only exists at.
    @include from-md {
      &.right .text { padding-right: $space-5; padding-left: $space-8; }
    }

    // Body copy shrinks below 1200px (jk-34) — the base $font-size-lg
    // (20px) started crowding the text column once the illustration panel
    // took its share of the card's width at these in-between sizes; custom
    // breakpoint, not a named $bp-* (local/one-off, same precedent as the
    // Home partner-band's 900/1200px rules).
    @media (max-width: 1199.98px) {
      .text .body { font-size: 1.125rem; }
    }

    // Decorative circular arrow chip (36px) — same shape as the arrow
    // variant's, but positioned absolutely (this card's content alternates
    // sides, so it can't live in a per-side header row) rather than removed
    // from grid flow by being inside .text/.media.
    .arrow-btn {
      position: absolute;
      top: $space-4;
      right: $space-4;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: $radius-pill;
      @include transition(transform);

      svg { width: 20px; height: 20px; display: block; }
    }
  }

  // ─── colour fills — light fills get dark ink text, dark fills (green,
  // blue) get light cream text so contrast holds regardless of variant.
  &.pink   { background: var(--color-card-pink);   color: var(--color-ink); }
  &.cream  { background: var(--color-card-cream);  color: var(--color-ink); }
  &.yellow { background: var(--color-card-yellow); color: var(--color-ink); }
  &.lime   { background: var(--color-card-lime);   color: var(--color-ink); }
  &.green  { background: var(--color-card-green);  color: var(--color-on-banner); }
  &.blue   { background: var(--color-card-blue);   color: var(--color-on-banner); }

  // Arrow-chip colour pairing — rendered by the arrow and program variants,
  // so every colour either actually uses needs a pairing here. Light fills
  // (pink/cream/yellow) get a dark accent circle; dark fills (green/blue)
  // get a light cream circle — same light/dark split as the card fills above.
  &.pink   .arrow-btn,
  &.cream  .arrow-btn,
  &.yellow .arrow-btn { background: var(--color-quote-accent); color: var(--color-card-cream); }
  &.green  .arrow-btn,
  &.blue   .arrow-btn { background: var(--color-bg-alt); color: var(--color-ink); }
  // Direct correction: `.lime` was missing from both groups above — its
  // arrow-btn had no background/colour at all, so the circle didn't render,
  // just a bare arrow glyph. Own rule (not folded into the pink/cream/yellow
  // group) since the requested colour — dark green — isn't the shared
  // `--color-quote-accent` those use; reuses `--color-card-green` (the same
  // dark green other cards use as a *fill*) as the chip background instead,
  // paired with the same cream glyph colour the accent-chip group uses.
  &.lime   .arrow-btn { background: var(--color-card-green); color: var(--color-card-cream); }
}
</style>
